/**
 * The router half of the training gate (spec 53 section 3.3).
 *
 * A gated user (required training outstanding) may be on the training pages
 * and nowhere else; every other address sends them to /training. The server
 * already answers 403 TRAINING_REQUIRED from every other route
 * (Middleware/RequireTraining.js), so this is not the gate itself. It is what
 * keeps the browser from asking for pages it cannot have.
 *
 * Which LAYOUT renders (the bare one or the shell) is decided in main.js from
 * the same store getter, so a gated user never mounts the shell's rail, bells
 * or live map, all of which poll.
 */
export const GATE_ROUTES = Object.freeze(['Training', 'TrainingModule']);

/** Where `to` should go instead, or true when it may proceed. */
export function trainingRedirect(to, gated) {
  if (!gated) return true;
  if (to && GATE_ROUTES.includes(to.name)) return true;
  return { name: 'Training', replace: true };
}

/**
 * Install the guard. `isGated` is asked on every navigation, because the
 * answer can change under a running app: the /users/me row first, then the
 * organization's own `training.gated`.
 */
export function installTrainingGuard(router, isGated) {
  router.beforeEach(to => trainingRedirect(to, isGated()));
}

/**
 * After the boot's who-am-I read: the router's first navigation ran before
 * the store knew who this is, so a gated user may be standing on /dispatch.
 * Move them before anything mounts.
 */
export async function settleTrainingGate(router, isGated) {
  // A first navigation that failed (a stale chunk, say) is staleChunk.js's
  // business, not a reason to fail the boot.
  if (typeof router.isReady === 'function') await router.isReady().catch(() => {});
  const current = router.currentRoute.value;
  const verdict = trainingRedirect(current, isGated());
  if (verdict !== true) await router.replace(verdict).catch(() => {});
}
